import React, { useState } from 'react';
import {
  Card,
  Table,
  Tabs,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Modal,
  message,
  Button,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import api from '../services/api';

const { Search } = Input;
const { TextArea } = Input;
const { Title, Text } = Typography;

const Questions = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('service');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // The question currently being answered, and the text typed so far.
  const [answering, setAnswering] = useState(null);
  const [answerText, setAnswerText] = useState('');

  const params = new URLSearchParams();
  if (page) params.append('page', page);
  if (searchTerm) params.append('search', searchTerm);
  if (statusFilter === 'answered') params.append('answered', '1');
  if (statusFilter === 'unanswered') params.append('unanswered', '1');

  const endpoint =
    activeTab === 'service' ? '/admin/service-questions' : '/admin/product-questions';

  const { data, isLoading } = useQuery(
    ['questions', activeTab, page, statusFilter, searchTerm],
    async () => {
      const response = await api.get(`${endpoint}?${params.toString()}`);
      return response.data;
    },
    { keepPreviousData: true },
  );

  const rows = data?.data || [];

  const deleteServiceMutation = useMutation((id) => api.delete(`/service-questions/${id}`), {
    onSuccess: () => {
      message.success('Question deleted successfully');
      queryClient.invalidateQueries(['questions']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to delete question');
    },
  });

  const deleteProductMutation = useMutation((id) => api.delete(`/product-comments/${id}`), {
    onSuccess: () => {
      message.success('Question deleted successfully');
      queryClient.invalidateQueries(['questions']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to delete question');
    },
  });

  const answerMutation = useMutation(
    ({ id, answer }) => {
      // Service questions and product questions are stored differently, so
      // each has its own endpoint.
      const url =
        activeTab === 'service'
          ? `/service-questions/${id}/answer`
          : `/product-comments/${id}/answer`;
      return api.put(url, { answer });
    },
    {
      onSuccess: () => {
        message.success('Answer saved');
        setAnswering(null);
        setAnswerText('');
        queryClient.invalidateQueries(['questions']);
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to save the answer');
      },
    },
  );

  const openAnswer = (record) => {
    setAnswering(record);
    setAnswerText(record.answer || '');
  };

  const submitAnswer = () => {
    const answer = answerText.trim();
    if (!answer) {
      message.warning('Please type an answer first');
      return;
    }
    answerMutation.mutate({ id: answering.id, answer });
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete question',
      content: 'Are you sure you want to delete this question? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        if (activeTab === 'service') {
          deleteServiceMutation.mutate(record.id);
        } else {
          deleteProductMutation.mutate(record.id);
        }
      },
    });
  };

  const customerName = (record) =>
    record.customer?.full_name ||
    record.user?.full_name ||
    record.user?.name ||
    record.customer?.name ||
    'Customer';

  const providerName = (record) =>
    record.product_service?.provider?.full_name ||
    record.product_service?.provider?.name ||
    'Unknown';

  const itemName = (record) => record.product_service?.name || record.service?.name || '-';

  const questionText = (record) => record.question || record.comment || '';

  const columns = [
    {
      title: 'Customer',
      render: customerName,
    },
    {
      title: 'Provider',
      render: providerName,
    },
    {
      title: activeTab === 'service' ? 'Service' : 'Product',
      render: itemName,
    },
    {
      title: 'Question',
      render: questionText,
    },
    {
      title: 'Status',
      render: (_, record) =>
        record.answer ? <Tag color="green">Answered</Tag> : <Tag color="orange">Unanswered</Tag>,
    },
    {
      title: 'Answer',
      render: (_, record) =>
        record.answer ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{record.answer}</Text>
        ) : (
          <Text type="secondary">Not answered yet</Text>
        ),
    },
    {
      title: 'Asked On',
      dataIndex: 'created_at',
      render: (v) => (v ? dayjs(v).format('MMM DD, YYYY') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type={record.answer ? 'default' : 'primary'}
            size="small"
            icon={record.answer ? <EditOutlined /> : <MessageOutlined />}
            onClick={() => openAnswer(record)}
          >
            {record.answer ? 'Edit answer' : 'Answer'}
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>
        <QuestionCircleOutlined style={{ marginRight: 8 }} />
        Questions & Answers
      </Title>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setPage(1);
        }}
        items={[
          { key: 'service', label: 'Service Questions' },
          { key: 'product', label: 'Product Questions' },
        ]}
        style={{ marginBottom: 16 }}
      />
      <Card>
        <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Select
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            style={{ width: 160 }}
            options={[
              { value: '', label: 'All Questions' },
              { value: 'answered', label: 'Answered' },
              { value: 'unanswered', label: 'Unanswered' },
            ]}
          />
          <Search
            placeholder="Search questions or answers"
            allowClear
            enterButton={<SearchOutlined />}
            style={{ width: 300 }}
            onSearch={(v) => {
              setSearchTerm(v);
              setPage(1);
            }}
          />
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={isLoading}
          pagination={{
            current: data?.current_page || 1,
            total: data?.total || 0,
            pageSize: data?.per_page || 15,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          locale={{ emptyText: <Text type="secondary">No questions found</Text> }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Modal
        open={!!answering}
        title={answering?.answer ? 'Edit answer' : 'Answer question'}
        okText={answering?.answer ? 'Save answer' : 'Post answer'}
        cancelText="Cancel"
        confirmLoading={answerMutation.isLoading}
        onCancel={() => {
          setAnswering(null);
          setAnswerText('');
        }}
        onOk={submitAnswer}
        width={640}
        destroyOnClose
      >
        {answering && (
          <>
            <p style={{ marginBottom: 4 }}>
              <Text strong>{itemName(answering)}</Text>{' '}
              <Text type="secondary">({providerName(answering)})</Text>
            </p>
            <p style={{ marginBottom: 12, color: '#595959' }}>
              {customerName(answering)}: {questionText(answering)}
            </p>
            <TextArea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Type your answer. The customer sees this on the listing."
              autoSize={{ minRows: 4, maxRows: 10 }}
              maxLength={1000}
              showCount
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default Questions;
